import PropTypes from 'prop-types';
import React from 'react';

import autobind from "autobind-decorator";

import {connect} from "react-redux";
import {bindActionCreators} from "redux";

import {clearToolTip, setToolTip} from "../store/actions";

import number from 'abusesa-js/formats/number';
import {strictIsTime, looseIsTime, looseParseTime, format as formatTime, timeformats} from "../lib/TimeUtils";
import ColorMap from "../lib/ColorMap";
import {Surface, Layer, FlexLayerGroup} from "./Canvas.jsx";
import Text from "./Text.jsx";
import Legend, {estimateSize as estimateLegendSize} from "./Legend.jsx";

import Mappable from "lib/Mappable";


class ChartContentWrapper extends FlexLayerGroup {
    doDraw() {

    }

    render() {
        return <Layer>{this.props.children}</Layer>;
    }
}


const HoverCache = new WeakMap();

export class ChartBaseContent extends Layer {
    get defaultStyles() {
        return {
            flex: 1
        };
    }

    _reverseLookup = null;
    _drawHoveredItems = null;

    getHoverData({x, y}) {
        if (!this._reverseLookup) {
            return null;
        }

        let cached = HoverCache.get(this);
        if (cached && cached.item
            && typeof cached.isValid === "function"
            && cached.isValid(x, y)
        ) {
            return cached.item;
        } else {
            let res = this._reverseLookup(x, y);
            HoverCache.set(this, res);
            return res ? res.item : null;
        }
    }

    doDraw() {
        HoverCache.delete(this);
    }

    get pureProps() {
        return ["data", "colors", "groups", "hoveredItem", "type"];
    }

    *getChildCanvases() {
        let {hoveredItem} = this.props;

        this.draw();

        let base = {position: {x: 0, y: 0}, opacity: 1};

        let drawHoveredItems = this._drawHoveredItems && hoveredItem;

        yield {...base, canvas: this.getCanvas(), opacity: drawHoveredItems ? 0.3 : 1};

        if (drawHoveredItems) {
            let hoverCanvas = document.createElement('canvas');

            hoverCanvas.width = this.getCanvas().width;
            hoverCanvas.height = this.getCanvas().height;

            let ctx = hoverCanvas.getContext("2d");
            this._rescale(ctx);
            this._drawHoveredItems(ctx, hoveredItem);

            yield {...base, canvas: hoverCanvas};
        }
    }

    cleanup(){
        super.cleanup();
        this._drawHoveredItems = null;
        HoverCache.delete(this);
    }

    markDirty() {
        this._drawHoveredItems = null;
        super.markDirty();
    }

    componentWillReceiveProps(nextProps) {
        let oldProps = this.props;

        for (let prop of this.pureProps) {
            if (oldProps[prop] !== nextProps[prop]) {
                if (prop === "hoveredItem" && this._drawHoveredItems) {
                    this.requestRedraw();
                } else {
                    this.markDirty();
                    return;
                }
            }
        }
    }
}


//check for yyyy, yyyy-mm or yyyy-qq
const inaccurateTimeRe = /^(\d{4})(-(\d\d?|Q\d))?$/i;

const isInnaccurateTime = string => {
    return string && inaccurateTimeRe.test(string);
};

const isNumeric = string => {
    return number.parse("" + string) !== null;
};

const ascending = (a, b) => a < b ? -1 : a > b ? 1 : a >= b ? 0 : NaN;

const cmp = (a, b) => {
    if (strictIsTime(a)) {
        let t1 = looseParseTime(a), t2 = looseParseTime(b);
        return t1 - t2;
    } else if (Number(a) && Number(b)) {
        return Number(a) - Number(b);
    } else {
        return ascending(a, b);
    }
};

const SurfaceWithTooltips = connect(null,
    dispatch => ({
        clearToolTip: bindActionCreators(clearToolTip, dispatch),
        setToolTip: bindActionCreators(setToolTip, dispatch)
    }), null, {withRef: true})(Surface);

export default function WrapChartContent(ChartContent = ChartContentWrapper) {

    return class Chart extends React.PureComponent {
        static propTypes = {
            data: PropTypes.object,
            granularity: PropTypes.string,
            colors: PropTypes.object,
            isSelected: PropTypes.bool,
            stack: PropTypes.bool,
            title: PropTypes.string,
            onMouseMove: PropTypes.func,
            onMouseLeave: PropTypes.func,
            onMouseEnter: PropTypes.func
        };

        static defaultProps = {
            colors: {},
            data: null,
            isSelected: true
        };


        static displayName = 'Chart';

        static get options() {
            let childopts = ChartContent.options || {};

            return {
                topCount: 5,
                ...childopts
            };
        }

        static get configOptions() {
            let childconfig = ChartContent.configOptions;

            return [
                "title",
                ...childconfig
            ];
        }

        _cleanupTimer = null
        _resizeTimer = null

        constructor(...args) {
            super(...args);

            this.state = {
                width: 0,
                height: 0,
                colors: new ColorMap({
                    map: this.props.colors
                })
            };
        }

        takeScreenshot() {
            let ref = this.surfaceRef;
            while (typeof ref.getWrappedInstance === "function") {
                ref = ref.getWrappedInstance();
            }
            ref.takeScreenshot(this.getTitleText());
        }

        cleanup() {
            let ref = this.surfaceRef;
            while (typeof ref.getWrappedInstance === "function") {
                ref = ref.getWrappedInstance();
            }

            ref.cleanup();

            if (this._cleanupTimer) {
                clearTimeout(this._cleanupTimer);
                this._cleanupTimer = null;
            }
        }

        @autobind
        updateSize() {
            let update = () => {
                this._resizeTimer = null;

                let size = {
                    width: this.containerRef.clientWidth,
                    height: this.containerRef.clientHeight
                };

                if (this.state.width != size.width || this.state.height != size.height) {
                    this.setState(size);
                }
            };

            if (!this._resizeTimer) {
                this._resizeTimer = setTimeout(update, this.props.isSelected ? 100 : 1000);
            }
        }

        componentDidUpdate() {
            this.updateSize();
        }

        componentWillUnmount() {
            clearTimeout(this._resizeTimer);
            clearTimeout(this._cleanupTimer);

            window.removeEventListener("resize", this.updateSize);
        }

        componentDidMount() {
            window.addEventListener("resize", this.updateSize);
            this.updateSize();
            this.componentWillReceiveProps(this.props);
        }

        componentWillReceiveProps(nextProps) {
            let oldData = this.props.data;
            let {data, stack} = nextProps;

            if (data != oldData || !this.state.groups || !this.state.names || !this.state.data || stack !== this.props.stack) {
                let gkey = data.keys("group").join(",") || "";
                let ykey = data.keys("y");
                let xkey = data.keys("x");
                let groups = [];

                if (gkey) {
                    data = data.sort(function(a, b) {
                        let key = a[xkey] != b[xkey] ? xkey : gkey;
                        return cmp(a[key], b[key]);
                    });

                    groups = (data.collapse("x").values() || [])
                        .map(g => ({name: g[gkey], value: g[ykey]}))
                        .sort((a, b) => ascending(a.name, b.name));
                }

                let colors = new ColorMap({map: this.props.colors})
                    .domain(groups.map(g => g.name));

                let names = data.collapse("group").values("x")
                    .filter(val => val !== void 0)
                    .map(String)
                    .sort(cmp);

                let nameScale = names.length > 2 && names.every(looseIsTime) ? "time" : "ordinal";

                let {granularity} = nextProps;

                if (names.length > 0 && granularity && names.every(isInnaccurateTime)) {
                    nameScale = "ordinal";
                    let minmax = [names[0], names[names.length - 1]].map(looseParseTime),
                        format = formatTime(timeformats[granularity] || granularity),
                        tmp = new Date(minmax[0]);

                    names = [];

                    do {
                        names.push(format(tmp));
                        tmp.setUTCMonth(tmp.getUTCMonth() + 1);
                    } while (tmp <= minmax[1]);

                    names = names
                        .filter((item, index, arr) => arr.indexOf(item) === index)
                        .sort(cmp);
                } else if (names.length > 0 && names.every(isNumeric)) {
                    nameScale = "numeric";
                }

                this.setState({names, nameScale, data, groups, colors});
            }

            if (this._cleanupTimer) {
                clearTimeout(this._cleanupTimer);
            }

            this._cleanupTimer = setTimeout(() => this.cleanup(), 10000);
        }

        getTitleText() {
            let {data, title} = this.props;

            if (!title) {
                let xkey = data.keys("x").join(",") || "";
                let ykey = data.keys("y").join(",") || "";
                let gkey = data.keys("group").join(",") || "";

                title = ykey;
                title += xkey ? " by " + xkey : "";
                title += xkey && gkey ? "," : "";
                title += gkey ? " grouped by " + gkey : "";
            }

            return title;
        }

        render() {
            let {width, height, colors, groups, names, nameScale, data} = this.state;
            let {isSelected} = this.props;

            let mouseProps = new Mappable(this.props)
                .filter((val, key) => ["onMouseMove", "onMouseLeave", "onMouseEnter"].includes(key))
                .toObject();

            let excludedProps = ["data", "colors", "children", "title", "type", ...Object.keys(mouseProps)];
            let extraProps = new Mappable(this.props)
                .filter((val, key) => !excludedProps.includes(key))
                .toObject();

            if (!data) {
                return <div className="chart-container" ref={el => this.containerRef = el}></div>;
            }

            let ykey = data.keys("y").join(",") || "";
            let gkey = data.keys("group").join(",") || "";

            return (
                <div className="chart-container" ref={el => this.containerRef = el} {...mouseProps}>
                    <SurfaceWithTooltips ref={el => this.surfaceRef = el} width={width} height={height} isSelected={isSelected}>
                        <FlexLayerGroup style={{flexDirection: "column", margin: 15, alignItems: "stretch"}}>
                            <Text style={{fontSize: 20, alignSelf: "center"}}>{this.getTitleText()}</Text>
                            <FlexLayerGroup style={{flexDirection: "row", alignItems: "stretch"}}>
                                <ChartContent
                                    {...extraProps}
                                    colors={colors}
                                    data={data}
                                    names={names}
                                    groups={groups}
                                    nameScale={nameScale}
                                    estimatedWidth={width - estimateLegendSize(groups, height - 100).width - 30}
                                />
                                <Legend
                                    style={{alignSelf: "center"}}
                                    groups={groups}
                                    colors={colors}
                                    keys={{name: gkey, value: ykey}}
                                    maxHeight={height - 100}
                                />
                            </FlexLayerGroup>
                        </FlexLayerGroup>
                    </SurfaceWithTooltips>
                </div>
            );
        }
    };
}


// WEBPACK FOOTER //
// ./src/kpi/charts/Chart.jsx